import type { ForkOverridesShape } from './fire-forks-types';

export const DEFAULT_SCENARIO_NAME = 'Custom scenario';
export const MAX_SCENARIO_NAME_LENGTH = 120;

export type ScenarioSummaryLabelMaps = {
  wageStreamNames: Map<string, string>;
  holdingLabels: Map<string, string>;
  lifeEventNames: Map<string, string>;
};

export function summarizeScenarioOverrides(
  overrides: ForkOverridesShape,
  { wageStreamNames, holdingLabels, lifeEventNames }: ScenarioSummaryLabelMaps,
): string[] {
  const bullets: string[] = [];
  const s = overrides.scalars ?? {};

  function describePct(multiplier: number, what: string): string | null {
    const p = Math.round((multiplier - 1) * 100);
    if (p === 0) return null;
    const verb = p > 0 ? 'Increase' : 'Reduce';
    return `${verb} ${what} by ${Math.abs(p)}%`;
  }

  const disc =
    s.discretionaryMultiplier != null
      ? describePct(s.discretionaryMultiplier, 'discretionary spending')
      : null;
  if (disc) bullets.push(disc);

  const wage =
    s.wageMultiplier != null
      ? describePct(s.wageMultiplier, 'wage income')
      : null;
  if (wage) bullets.push(wage);

  const all =
    s.incomeMultiplier != null
      ? describePct(s.incomeMultiplier, 'all income')
      : null;
  if (all) bullets.push(all);

  const holdings =
    s.holdingsMultiplier != null
      ? describePct(s.holdingsMultiplier, 'market holdings')
      : null;
  if (holdings) bullets.push(holdings);

  if (s.targetFireYearDelta != null && s.targetFireYearDelta !== 0) {
    const n = Math.abs(s.targetFireYearDelta);
    const noun = n === 1 ? 'year' : 'years';
    bullets.push(
      `Retire ${n} ${noun} ${s.targetFireYearDelta < 0 ? 'earlier' : 'later'}`,
    );
  }
  if (overrides.persons?.primary?.state) {
    bullets.push(`Move to ${overrides.persons.primary.state}`);
  }
  if (overrides.persons?.spouse?.state) {
    bullets.push(`Spouse moves to ${overrides.persons.spouse.state}`);
  }
  for (const id of overrides.droppedIncomeStreamIds ?? []) {
    const name = wageStreamNames.get(id);
    bullets.push(name ? `Leave job: ${name}` : 'Leave a job');
  }
  for (const pause of overrides.incomeStreamPauses ?? []) {
    const name = wageStreamNames.get(pause.incomeStreamId);
    const endYear = pause.resumeYear - 1;
    const span =
      endYear <= pause.startYear
        ? `in ${pause.startYear}`
        : `from ${pause.startYear} through ${endYear}`;
    bullets.push(
      name ? `Pause income: ${name} ${span}` : `Pause an income stream ${span}`,
    );
  }
  for (const added of overrides.addedIncomeStreams ?? []) {
    const monthly = Math.round(added.monthlyAmount).toLocaleString();
    bullets.push(`New income: ${added.name} (~$${monthly}/mo)`);
  }
  for (const po of overrides.holdingPriceOverrides ?? []) {
    const label = holdingLabels.get(po.holdingId) ?? 'Largest holding';
    const price =
      po.overridePrice >= 10
        ? `$${Math.round(po.overridePrice).toLocaleString()}`
        : `$${po.overridePrice.toFixed(2)}`;
    bullets.push(`${label} at ${price}`);
  }
  for (const id of overrides.excludedLifeEventIds ?? []) {
    const name = lifeEventNames.get(id);
    bullets.push(name ? `Skip life event: ${name}` : 'Skip a life event');
  }
  for (const event of overrides.addedLifeEvents ?? []) {
    bullets.push(`Add life event: ${event.description}`);
  }
  return bullets;
}

export function buildScenarioNameFromSummary(
  bullets: string[],
  fallback = DEFAULT_SCENARIO_NAME,
): string {
  const summary = bullets.map((bullet) => bullet.trim()).filter(Boolean);
  if (summary.length === 0) return fallback;

  const suffix = summary.length > 1 ? ` + ${summary.length - 1} more` : '';
  return trimScenarioName(`${summary[0]}${suffix}`);
}

function trimScenarioName(name: string): string {
  if (name.length <= MAX_SCENARIO_NAME_LENGTH) return name;
  return `${name.slice(0, MAX_SCENARIO_NAME_LENGTH - 3).trimEnd()}...`;
}
